import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { ESCALATION_EVENT_NOTE_MAX_LENGTH } from './schema';

// ─── claiming an escalation ─────────────────────────────────────────────────
//
// Wire vocabulary for care staff claiming an open safety escalation, releasing
// a claim they hold, and taking over a claim someone else holds. "In progress"
// on the staff surfaces is an open escalation with a claim; the review
// `status` never changes here.
//
// Every request names the escalation by path and the actor by the staff gate,
// never by body: the bodies below are `.strict()`, so a body that names an
// actor or a holder is refused rather than ignored (ADR-135 Rule 1, ADR-087).
//
// The responses carry the outcome and this escalation's claim, and nothing
// about the member. The Escalation Review list calls these routes without
// opening the member page, and only the member UUID may appear there.

/** How a claim attempt ended. Every value is a 200: a refusal is a normal answer. */
export const ESCALATION_CLAIM_OUTCOME = [
  'claimed',
  'already_claimed_by_you',
  'claimed_by_other',
  'already_handled',
] as const;
export const escalationClaimOutcomeSchema = z.enum(ESCALATION_CLAIM_OUTCOME);
export type EscalationClaimOutcome = z.infer<typeof escalationClaimOutcomeSchema>;

/** How a release attempt ended. Only the holder can release. */
export const ESCALATION_RELEASE_OUTCOME = [
  'released',
  'not_claimed',
  'claimed_by_other',
  'already_handled',
] as const;
export const escalationReleaseOutcomeSchema = z.enum(ESCALATION_RELEASE_OUTCOME);
export type EscalationReleaseOutcome = z.infer<typeof escalationReleaseOutcomeSchema>;

/**
 * How a take-over attempt ended. `claim_changed` means the claim the caller
 * confirmed taking is no longer the current one — released, or taken by
 * someone else — so nothing was taken from anybody.
 */
export const ESCALATION_TAKE_OVER_OUTCOME = [
  'taken_over',
  'already_claimed_by_you',
  'claim_changed',
  'already_handled',
] as const;
export const escalationTakeOverOutcomeSchema = z.enum(ESCALATION_TAKE_OVER_OUTCOME);
export type EscalationTakeOverOutcome = z.infer<typeof escalationTakeOverOutcomeSchema>;

/**
 * The optional reason on a release or a take-over. Trimmed, and refused when
 * blank: a client that has nothing to say omits the field rather than sending
 * whitespace that would be stored as a reason.
 */
export const escalationClaimReasonSchema = z
  .string()
  .trim()
  .min(1)
  .max(ESCALATION_EVENT_NOTE_MAX_LENGTH);

/** Claiming takes no input beyond the path. */
export const escalationClaimRequestSchema = z.object({}).strict();
export type EscalationClaimRequest = z.input<typeof escalationClaimRequestSchema>;

export const escalationReleaseRequestSchema = z
  .object({ reason: escalationClaimReasonSchema.optional() })
  .strict();
export type EscalationReleaseRequest = z.input<typeof escalationReleaseRequestSchema>;

/**
 * `claimId` is the claim the caller was shown and confirmed taking over. The
 * write applies only while it is still the current claim, so a take-over can
 * never silently take a claim that changed hands while the dialog was open.
 */
export const escalationTakeOverRequestSchema = z
  .object({
    claimId: z.string().uuid(),
    reason: escalationClaimReasonSchema.optional(),
  })
  .strict();
export type EscalationTakeOverRequest = z.input<typeof escalationTakeOverRequestSchema>;

/**
 * One escalation's current claim, as staff see it.
 *
 * `holderName` is the server-resolved display name — staff names are allowed
 * on these staff surfaces, and the client never resolves staff ids. No staff
 * id travels at all: whether the viewer is the holder is the server-computed
 * `heldByYou`. `claimId` is the opaque token a take-over names. A holder who
 * has since been deactivated still holds the claim by name; `deactivated` says
 * so, so the claim does not read as covered.
 *
 * Not `.strict()`: Atlas parses responses with `schema.parse()`, so a strict
 * response would fail every bundle older than a deploy that adds a field
 * (ADR-135 Rule 1).
 */
export const escalationClaimSchema = z.object({
  claimId: z.string().uuid(),
  // No `.min(1)`: this schema rides the member read, and a holder whose
  // display name resolved empty must not fail the whole member page.
  holderName: z.string(),
  claimedAt: z.iso.datetime(),
  heldByYou: z.boolean(),
  // Server-decided: the holder's staff account is disabled or removed, so the
  // claim no longer means anyone is working the escalation. Optional because an
  // API deployed before the field sends none, which reads as not deactivated.
  deactivated: z.boolean().optional(),
});
export type EscalationClaim = z.infer<typeof escalationClaimSchema>;

/** `claim` is null when the escalation is free (or handled). Not strict, as above. */
export const escalationClaimDataSchema = z.object({
  outcome: escalationClaimOutcomeSchema,
  claim: escalationClaimSchema.nullable(),
});
export type EscalationClaimData = z.infer<typeof escalationClaimDataSchema>;
export const escalationClaimResponseSchema = apiEnvelope(escalationClaimDataSchema);
export type EscalationClaimResponse = z.infer<typeof escalationClaimResponseSchema>;

export const escalationReleaseDataSchema = z.object({
  outcome: escalationReleaseOutcomeSchema,
  claim: escalationClaimSchema.nullable(),
});
export type EscalationReleaseData = z.infer<typeof escalationReleaseDataSchema>;
export const escalationReleaseResponseSchema = apiEnvelope(escalationReleaseDataSchema);
export type EscalationReleaseResponse = z.infer<typeof escalationReleaseResponseSchema>;

export const escalationTakeOverDataSchema = z.object({
  outcome: escalationTakeOverOutcomeSchema,
  claim: escalationClaimSchema.nullable(),
});
export type EscalationTakeOverData = z.infer<typeof escalationTakeOverDataSchema>;
export const escalationTakeOverResponseSchema = apiEnvelope(escalationTakeOverDataSchema);
export type EscalationTakeOverResponse = z.infer<typeof escalationTakeOverResponseSchema>;

// ─── escalation managers: release and assign ────────────────────────────────
//
// An escalation manager can release a claim someone else holds, and assign an
// open escalation to a named person who may handle escalations. Both act on the
// claim, never on the review `status`, and both name the claim the manager was
// shown, so a claim that changed hands while the dialog was open is refused
// rather than released or replaced silently.
//
// The assign body names its assignee by internal user id: the write has to say
// whom. It still never names the actor, which comes from the staff gate, and
// the responses still carry no staff id.

/**
 * How a manager release ended. `held_by_you` is the caller's own claim, which
 * they release the ordinary way, so the right event kind is recorded.
 */
export const ESCALATION_MANAGER_RELEASE_OUTCOME = [
  'released',
  'claim_changed',
  'held_by_you',
  'already_handled',
] as const;
export const escalationManagerReleaseOutcomeSchema = z.enum(ESCALATION_MANAGER_RELEASE_OUTCOME);
export type EscalationManagerReleaseOutcome = z.infer<typeof escalationManagerReleaseOutcomeSchema>;

/**
 * How an assignment ended. `assignee_unavailable` is one answer for an id that
 * names nobody, a removed or disabled person, or someone who may not handle
 * escalations, so the route says nothing about why a given id is ineligible.
 */
export const ESCALATION_ASSIGN_OUTCOME = [
  'assigned',
  'claim_changed',
  'already_assigned',
  'assignee_unavailable',
  'already_handled',
] as const;
export const escalationAssignOutcomeSchema = z.enum(ESCALATION_ASSIGN_OUTCOME);
export type EscalationAssignOutcome = z.infer<typeof escalationAssignOutcomeSchema>;

/** `claimId` is the claim the manager was shown and confirmed releasing. */
export const escalationManagerReleaseRequestSchema = z
  .object({
    claimId: z.string().uuid(),
    reason: escalationClaimReasonSchema.optional(),
  })
  .strict();
export type EscalationManagerReleaseRequest = z.input<typeof escalationManagerReleaseRequestSchema>;

/**
 * `expectedClaimId` is required and nullable: null asserts the manager saw the
 * escalation free, so an omitted field cannot be read as either answer.
 */
export const escalationAssignRequestSchema = z
  .object({
    assigneeInternalUserId: z.string().uuid(),
    expectedClaimId: z.string().uuid().nullable(),
    reason: escalationClaimReasonSchema.optional(),
  })
  .strict();
export type EscalationAssignRequest = z.input<typeof escalationAssignRequestSchema>;

/** Not strict, as the claim responses above. */
export const escalationManagerReleaseDataSchema = z.object({
  outcome: escalationManagerReleaseOutcomeSchema,
  claim: escalationClaimSchema.nullable(),
});
export type EscalationManagerReleaseData = z.infer<typeof escalationManagerReleaseDataSchema>;
export const escalationManagerReleaseResponseSchema = apiEnvelope(
  escalationManagerReleaseDataSchema,
);
export type EscalationManagerReleaseResponse = z.infer<
  typeof escalationManagerReleaseResponseSchema
>;

export const escalationAssignDataSchema = z.object({
  outcome: escalationAssignOutcomeSchema,
  claim: escalationClaimSchema.nullable(),
});
export type EscalationAssignData = z.infer<typeof escalationAssignDataSchema>;
export const escalationAssignResponseSchema = apiEnvelope(escalationAssignDataSchema);
export type EscalationAssignResponse = z.infer<typeof escalationAssignResponseSchema>;

/**
 * One person an escalation may be assigned to. The id is what the assign body
 * names; the display name is server-resolved, as a holder's is. Not strict.
 */
export const escalationAssigneeSchema = z.object({
  internalUserId: z.string().uuid(),
  displayName: z.string(),
});
export type EscalationAssignee = z.infer<typeof escalationAssigneeSchema>;

export const escalationAssigneesDataSchema = z.object({
  assignees: z.array(escalationAssigneeSchema),
});
export type EscalationAssigneesData = z.infer<typeof escalationAssigneesDataSchema>;
export const escalationAssigneesResponseSchema = apiEnvelope(escalationAssigneesDataSchema);
export type EscalationAssigneesResponse = z.infer<typeof escalationAssigneesResponseSchema>;
